import merge from 'lodash/merge';

import { REPORTS } from '@/app/dashboards/utils/reports.enum';
import {
  CONVERSATIONAL_RESPONSE_TYPES_MIXPANEL,
  INTENT_TYPES,
  REQUEST_CONTEXT_BUTTON_IDENTIFIERS,
  SUMMARY_INTENT_TYPES,
  VISION_AI_INTENT_TYPE_NAMES,
  VISION_AI_MIXPANEL_EVENTS,
} from '@/components/VisionAi/constants';
import { CHANNELS } from '@/models/dashboards/channels.enum';
import { useAuthStore } from '@/stores/auth';
import { useCommunityInteractionStore } from '@/stores/community-interactions';
import { useDashboardsStore } from '@/stores/dashboards';
import { useIntercomStore } from '@/stores/intercom';
import { useSearchStore } from '@/stores/search';
import { useSocialListeningStore } from '@/stores/social-listening';
import { useTrackingStore } from '@/stores/tracking';
import { getMetricDetails } from '@/utils/metrics';

export function widgetLaunchEvent({ interaction, path }) {
  const trackingStore = useTrackingStore();
  trackingStore.track(VISION_AI_MIXPANEL_EVENTS.AI_WIDGET_LAUNCH, { interaction, path });
}

export function aiWidgetInteractionEvent({
  interaction,
  summaryCount = null,
  renameChat = null,
  conversationName = null,
}) {
  const trackingStore = useTrackingStore();
  trackingStore.track(VISION_AI_MIXPANEL_EVENTS.AI_WIDGET_INTERACTION, {
    interaction,
    summaryCount,
    renameChat,
    conversationName,
  });
}

const BUTTON_IDENTIFIER_TO_SUMMARY_TYPE = {
  [REQUEST_CONTEXT_BUTTON_IDENTIFIERS.SUMMARIZE_COMMUNITY_INSIGHTS]: 'Community Insights Summary',
  [REQUEST_CONTEXT_BUTTON_IDENTIFIERS.SUMMARIZE_COMMUNITY_MESSAGES]: 'Community Inbox Summary',
};

function getPlatformSuperProperties(intentType) {
  const communityInteractionStore = useCommunityInteractionStore();
  const dashboardsStore = useDashboardsStore();
  const socialListeningStore = useSocialListeningStore();

  const payload = {};

  switch (intentType) {
    case INTENT_TYPES.DASHBOARD_REPORTS:
      payload.dashboardsId = dashboardsStore.currentDashboardId;
      payload.dashboardsName = dashboardsStore.currentDashboardName;
      break;
    case INTENT_TYPES.COMMENTS:
    case INTENT_TYPES.DMS:
      payload.communityInteractionId = communityInteractionStore.selectedCommunityInteractionId;
      break;
    case INTENT_TYPES.TOPICS:
      payload.topicId = socialListeningStore.selectedTopic?.id;
      payload.topicName = socialListeningStore.selectedTopic?.name;
      break;
    default:
      break;
  }
  return payload;
}

function generateAssistantMessageReceivedPayload(reports) {
  const authStore = useAuthStore();
  const searchStore = useSearchStore();

  return reports.reduce(
    (acc, report) => {
      const reportMeta = report?.meta;
      if (!reportMeta) return acc;

      const brandIds = reportMeta.brand_ids;
      if (brandIds) {
        const allBrandNames = acc.questionBrands.concat(authStore.getBrandNames(brandIds));
        acc.questionBrands = [...new Set(allBrandNames)];
      }

      const competitorIds = reportMeta.competitor_source_account_ids;
      if (competitorIds) {
        const source = CHANNELS[reportMeta.channels[0]].competitiveChannelType;
        const allCompetitorNames = acc.questionCompetitors.concat(
          searchStore.getCompetitorNames(competitorIds, source),
        );
        acc.questionCompetitors = [...new Set(allCompetitorNames)];
      }

      const allChannels = acc.questionChannels.concat(reportMeta.channels);
      const allMetrics = acc.questionMetrics.concat(reportMeta.metrics);

      const mediaType = getMetricDetails(
        reportMeta.metrics[0],
        reportMeta.channels,
        REPORTS[report.type].metricTypeReportKey,
      ).mediaTypeLabel;
      if (mediaType) {
        const allMediaTypes = acc.questionMediaTypes.concat([mediaType]);
        acc.questionMediaTypes = [...new Set(allMediaTypes)];
      }

      acc.questionChannels = [...new Set(allChannels)];
      acc.questionMetrics = [...new Set(allMetrics)];
      acc.visualResponses.push(report.type);

      return acc;
    },
    {
      visualResponses: [],
      questionBrands: [],
      questionCompetitors: [],
      questionChannels: [],
      questionMetrics: [],
      questionMediaTypes: [],
    },
  );
}

export function aiMessageInteractionEvent({ interaction, intentType, messageId }) {
  const trackingStore = useTrackingStore();
  const payload = merge(
    {
      intentType: VISION_AI_INTENT_TYPE_NAMES[intentType],
      messageId,
    },
    getPlatformSuperProperties(intentType),
  );
  if (interaction) payload.interaction = interaction;

  trackingStore.track(VISION_AI_MIXPANEL_EVENTS.AI_MESSAGE_INTERACTION, payload);
}

export function aiUserFeedbackEvent({ feedback, message }) {
  const trackingStore = useTrackingStore();

  const payload = generateAssistantMessageReceivedPayload(Object.values(message.additionalContent));

  payload.intentType = VISION_AI_INTENT_TYPE_NAMES[INTENT_TYPES.METRIC_ANALYTICS];
  payload.messageId = message.id;
  payload.feedbackType = feedback;
  payload.conversationId = message.conversationId;
  payload.message = message.message;
  const visionEndpoints = [];
  if (message?.toolCalls?.executeCode) {
    visionEndpoints.push('Coding Agent');
  }
  if (message?.toolCalls?.querySocialAnalytics) {
    visionEndpoints.push('Dashboards');
  }
  payload.visionEndpoints = visionEndpoints;
  trackingStore.track(VISION_AI_MIXPANEL_EVENTS.ASSISTANT_MESSAGE_RECEIVED_USER_FEEDBACK, payload);
}

function getSummaryType(intentType, buttonIdentifier) {
  if (intentType === INTENT_TYPES.UNIFIED) {
    return BUTTON_IDENTIFIER_TO_SUMMARY_TYPE[buttonIdentifier] ?? 'Unified';
  }
  return VISION_AI_INTENT_TYPE_NAMES[intentType];
}

export function aiSummaryLaunchEvent({ intentType, launchedFrom, buttonIdentifier }) {
  if (!SUMMARY_INTENT_TYPES.includes(intentType) && intentType !== INTENT_TYPES.UNIFIED) return;
  const trackingStore = useTrackingStore();
  const payload = merge(
    {
      summaryType: getSummaryType(intentType, buttonIdentifier),
      launchedFrom,
      buttonIdentifier,
      intentType,
    },
    getPlatformSuperProperties(intentType),
  );

  trackingStore.track(VISION_AI_MIXPANEL_EVENTS.AI_SUMMARY_LAUNCH, payload);
}

export function userMessageSentEvent({
  messageSent,
  messageId,
  intentType,
  usedPrompt,
  followUps,
  conversationId,
}) {
  const trackingStore = useTrackingStore();
  const intercomStore = useIntercomStore();
  const payload = {
    messageId,
    intentType: VISION_AI_INTENT_TYPE_NAMES[intentType],
    followUps,
    conversationId,
  };
  if (usedPrompt) {
    payload.promptSelected = messageSent;
  } else {
    payload.messageTyped = messageSent;
  }
  trackingStore.track(VISION_AI_MIXPANEL_EVENTS.USER_MESSAGE_SENT, payload);
  intercomStore.trackEvent(VISION_AI_MIXPANEL_EVENTS.USER_MESSAGE_SENT, payload);
}

function getResponseType(payload) {
  if (payload.questionCompetitors.length >= 1)
    return CONVERSATIONAL_RESPONSE_TYPES_MIXPANEL.COMPETITOR_BRANDS;

  if (payload.questionBrands.length >= 1)
    return CONVERSATIONAL_RESPONSE_TYPES_MIXPANEL.OWNED_BRANDS;

  return CONVERSATIONAL_RESPONSE_TYPES_MIXPANEL.OTHER;
}

export function assistantMessageReceivedEvent({ message, conversation }) {
  const trackingStore = useTrackingStore();

  if (message.intentType !== INTENT_TYPES.METRIC_ANALYTICS || !message.additionalContent) return;

  const payload = generateAssistantMessageReceivedPayload(Object.values(message.additionalContent));

  // This event is always for Metric Analytics intent, but filtering on Mixpanel is simpler with
  // this field provided.
  payload.intentType = VISION_AI_INTENT_TYPE_NAMES[INTENT_TYPES.METRIC_ANALYTICS];
  payload.conversationId = conversation.id;
  payload.conversationName = conversation.name;
  payload.messageId = message.id;
  payload.response = message.message;
  payload.numberOfVisuals = payload.visualResponses.length;
  payload.responseType = getResponseType(payload);
  trackingStore.track(VISION_AI_MIXPANEL_EVENTS.ASSISTANT_MESSAGE_RECEIVED_METRICS, payload);
}
